/**
 * DAC profiles -- group 07.
 *
 * Eight converters chosen so that the *conversion mechanism* differs, not just
 * the badge: a summed eight-channel delta-sigma, two valve-output designs with
 * very different analogue chains, a segmented current-source array run without
 * a digital filter, a current-mode discrete output, two FPGA-front-end designs
 * and a clock-led design. Where a figure comes from published manufacturer data
 * it is tagged MANUFACTURER_SPEC via `confidence`; everything else is an
 * explicit estimate and says so in `provenanceNote`. Nothing here is a
 * measurement, and several of these manufacturers publish no distortion figure
 * at all -- that is stated per entry rather than papered over.
 */

import type { DacProfile } from '../../types/hardware';
import {
  AKM_FILTERS,
  ESS_FILTERS,
  FILTERS,
  HARMONIC_SHAPES,
  SIMPLE_FILTERS,
  TRANSFORMER_LINE,
  TUBE_LINE,
  makeDac,
  usd,
} from '../kit';

export const DACS_07: DacProfile[] = [
  makeDac({
    mfr: 'Matrix Audio',
    model: 'Element X2',
    price: usd(3299, 'APPROXIMATE', 2023, 'US street pricing has sat in the 3,000-3,500 USD band; the exact current figure is not confirmed here.'),
    year: 2021,
    country: 'China',
    architecture: 'delta-sigma',
    core: 'ESS ES9038PRO, all eight HyperStream II channels summed per side',
    outputStage: 'op-amp',
    dynamicRangeDb: 130,
    thdPlusNDb: -118,
    crosstalkDb: -140,
    outputVrmsSE: 2.2,
    outputVrmsBal: 4.4,
    outputImpedanceOhms: 22,
    balanced: true,
    dsd: 'dsd512',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000, 705600, 768000],
    filters: ESS_FILTERS(),
    jitterPsRms: 20,
    noiseShaperOrder: 6,
    idleToneLevel: 0.05,
    harmonicShape: HARMONIC_SHAPES.highFeedback,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5fa8e8',
    tags: ['ess', 'streamer-dac', 'summed-channels'],
    notes:
      'Eight converter channels are paralleled per side, so the uncorrelated modulator noise falls roughly 9 dB relative to a single channel and the audible floor is set by the analogue stage rather than by the core. Distortion comes from a heavily fed-back op-amp I/V and balanced line driver, which is why the modelled residue is high-order rather than the H2/H3 pair a discrete class-A stage would leave. The variable that actually changes this profile is filter choice: the ESS bank spans a 0.905-Nyquist sharp linear-phase design and a 0.72-Nyquist slow minimum-phase one, a difference of tens of samples of pre-ringing.',
    provenanceNote:
      'Dynamic range, THD+N, output level and channel separation as published by Matrix Audio for the Element X2; the filter set follows the ES9038PRO datasheet roll-off options. The high-order-dominant harmonic distribution is inferred from the feedback-dominated output stage, not measured, and the idle-tone and noise-shaper-order figures are architecture-typical estimates.',
  }),

  makeDac({
    mfr: 'Cayin',
    model: 'iDAC-6',
    price: usd(1299, 'APPROXIMATE', 2017, 'Launch-era US retail was close to 1,300 USD; treated as approximate because the figure could not be confirmed against a current price list.'),
    year: 2016,
    country: 'China',
    architecture: 'multibit-delta-sigma',
    core: 'Dual AKM AK4490EQ Velvet Sound switched-resistor converters, one per channel',
    outputStage: 'tube',
    dynamicRangeDb: 112,
    thdPlusNDb: -80,
    crosstalkDb: -95,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 700,
    balanced: true,
    dsd: 'dsd256',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    filters: AKM_FILTERS(),
    effectiveBits: 19,
    jitterPsRms: 80,
    slewRateVus: 9,
    harmonicShape: HARMONIC_SHAPES.tubeDac,
    response: TUBE_LINE(10, -1.0),
    confidence: 'ESTIMATED',
    accent: '#e2a24a',
    tags: ['akm', 'valve-output', 'selectable-stage'],
    notes:
      'A pair of 12AU7 double triodes forms a selectable valve path in parallel with a solid-state one, and this profile models the valve path. Two consequences dominate: an output impedance of several hundred ohms, high enough to interact with the input impedance and cable capacitance of whatever follows, and an asymmetric transfer curve whose second harmonic grows about 1 dB per dB of level instead of sitting at a fixed floor. The AK4490 switched-resistor output means the small-signal behaviour is multibit rather than 1-bit, so low-level linearity does not depend on dither the way a single-bit modulator does.',
    provenanceNote:
      'Converter complement, valve complement and the selectable output topology come from Cayin published material. Cayin does not publish a THD figure for the valve path, so the THD+N, dynamic range, output impedance and slew rate here are estimates typical of a 12AU7 line stage and are tagged ESTIMATED for the whole entry. The switched-resistor description follows AKM Velvet Sound documentation.',
  }),

  makeDac({
    mfr: 'Line Magnetic',
    model: 'LM-502CA',
    price: usd(2695, 'APPROXIMATE', 2020, 'US dealer pricing has been quoted in the 2,500-3,000 USD range; the exact figure is not confirmed.'),
    year: 2015,
    country: 'China',
    architecture: 'hybrid',
    core: 'Dual Wolfson WM8741 multi-bit sigma-delta converters feeding a valve gain stage with a valve-rectified supply',
    outputStage: 'transformer',
    dynamicRangeDb: 106,
    thdPlusNDb: -68,
    crosstalkDb: -78,
    outputVrmsSE: 2.4,
    outputVrmsBal: 4.8,
    outputImpedanceOhms: 600,
    balanced: true,
    dsd: 'dsd64',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    filters: [FILTERS.sharpLinear(128), FILTERS.slowLinear(48), FILTERS.slowMinimum(32)],
    effectiveBits: 18,
    jitterPsRms: 110,
    slewRateVus: 7,
    harmonicShape: HARMONIC_SHAPES.transformer,
    response: TRANSFORMER_LINE(14, 34000),
    confidence: 'ESTIMATED',
    accent: '#c4705a',
    tags: ['wolfson', 'valve-output', 'core-saturation'],
    notes:
      'The distinguishing element is the magnetics, not the converter. Finite primary inductance sets a first-order low-frequency corner near 14 Hz and leakage inductance a high-frequency corner near 34 kHz, so the passband is bounded at both ends by physical components rather than by a filter. Core hysteresis adds low-order distortion that rises as frequency falls and as level rises, which is the exact opposite of the frequency trend of an op-amp output stage, and the several-hundred-ohm source impedance makes the following input stage part of the transfer function.',
    provenanceNote:
      'Converter complement and the valve output stage come from Line Magnetic published material. Line Magnetic does not publish THD, dynamic range or output impedance for this model, so those are estimates typical of a transformer-coupled valve line stage. The transformer-coupled output is an inference from Line Magnetic design practice rather than a manufacturer claim, and is flagged here so it is not read as one.',
  }),

  makeDac({
    mfr: 'iFi Audio',
    model: 'Pro iDSD Signature',
    price: usd(3249, 'APPROXIMATE', 2022, 'US retail has been quoted around 3,000-3,500 USD; treated as approximate.'),
    year: 2021,
    country: 'United Kingdom',
    architecture: 'ladder-multibit',
    core: 'Quad Burr-Brown DSD1793 Advanced Segment converters in Bit-Perfect mode, Crysopeia FPGA ahead of them',
    outputStage: 'tube',
    dynamicRangeDb: 119,
    thdPlusNDb: -105,
    crosstalkDb: -115,
    outputVrmsSE: 2.1,
    outputVrmsBal: 4.6,
    outputImpedanceOhms: 200,
    balanced: true,
    dsd: 'dsd1024',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000, 705600, 768000],
    filters: [FILTERS.nos(), FILTERS.apodizing(128), FILTERS.slowMinimum(32)],
    defaultFilterId: 'nos',
    ladderMismatchLsb: 0.25,
    effectiveBits: 19,
    jitterPsRms: 30,
    harmonicShape: HARMONIC_SHAPES.tubeDac,
    response: TUBE_LINE(6, -0.5),
    confidence: 'ESTIMATED',
    accent: '#4fc3c7',
    tags: ['bit-perfect', 'nos', 'valve-output', 'fpga-front-end'],
    notes:
      'In Bit-Perfect mode the interpolation filter is removed from the path entirely, so the zero-order hold imposes its own sin(x)/x envelope (-3.9 dB at Nyquist at 44.1 kHz) and the images above Nyquist are left unsuppressed for the analogue stage to deal with. Because the converter is a segmented current-source array rather than a 1-bit modulator, small-signal linearity is limited by element matching, which this profile carries as a 0.25 LSB integral-nonlinearity term that does not shrink as the signal gets quieter. On top of that sits a GE5670 valve buffer contributing an asymmetric, H2-led residue.',
    provenanceNote:
      'Converter complement, the Bit-Perfect (non-oversampling) mode, the valve output and DSD1024 support come from iFi published material; the 119 dB dynamic range figure matches iFi published data but is tagged ESTIMATED here along with the rest of the entry because the THD+N, output impedance and jitter values are inferred rather than quoted. Classifying Advanced Segment as ladder-multibit is an authoring choice: it is a segmented current-source array, not an R-2R ladder, and is modelled that way because element mismatch rather than modulator noise sets its low-level linearity.',
  }),

  makeDac({
    mfr: 'Questyle',
    model: 'CMA Twelve Master',
    price: usd(2499, 'APPROXIMATE', 2021, 'US retail was quoted near 2,500 USD at launch; treated as approximate.'),
    year: 2020,
    country: 'China',
    architecture: 'delta-sigma',
    core: 'AKM AK4499EQ current-output converter',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 124,
    thdPlusNDb: -110,
    crosstalkDb: -120,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 10,
    balanced: true,
    dsd: 'dsd512',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    filters: AKM_FILTERS(),
    jitterPsRms: 40,
    slewRateVus: 45,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { amount: 0.34, asymmetry: 0.3, bias: 0.45, headroomDb: 15 },
    confidence: 'ESTIMATED',
    accent: '#a8c23a',
    tags: ['akm', 'current-mode', 'discrete-output'],
    notes:
      'The AK4499EQ is a current-output part, and the gain stage that follows it works on current rather than voltage, so its bandwidth is not bounded by the usual voltage-feedback gain-bandwidth trade and there is no slew-limited transient region at normal line levels. Modelled as a fast class-A stage: a short harmonic series that decays quickly past H3 and grows smoothly with level, plus a 10 ohm source impedance that makes the profile indifferent to what it is plugged into. It sits deliberately between the feedback-dominated Matrix profile and the valve entries in this group.',
    provenanceNote:
      'Converter part, the current-mode output topology and the AKM filter bank come from Questyle published material. Dynamic range, THD+N, output impedance and slew rate are estimates consistent with this class of design rather than quoted figures, so the entry is tagged ESTIMATED.',
  }),

  makeDac({
    mfr: 'Ferrum Audio',
    model: 'WANDLA',
    price: usd(2795, 'APPROXIMATE', 2023, 'US retail has been quoted close to 2,800 USD; treated as approximate.'),
    year: 2022,
    country: 'Poland',
    architecture: 'fpga-custom',
    core: 'ESS ES9038PRO under FPGA control, with an externally authored filter and digital volume engine ahead of it',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 125,
    thdPlusNDb: -113,
    crosstalkDb: -125,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 30,
    balanced: true,
    dsd: 'dsd256',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000, 705600, 768000],
    filters: [
      { ...FILTERS.apodizing(192), id: 'apod-authored', label: 'Apodizing, externally authored' },
      FILTERS.sharpLinear(128),
      FILTERS.slowMinimum(32),
      FILTERS.hybrid(96),
    ],
    jitterPsRms: 15,
    harmonicShape: HARMONIC_SHAPES.deltaSigma,
    confidence: 'ESTIMATED',
    accent: '#8f7ce0',
    tags: ['fpga', 'authored-filters', 'digital-volume'],
    notes:
      'The FPGA sits between the input and the converter, so the reconstruction filter and the volume law are written by the designer instead of being inherited from the chip filter bank. Volume is applied in the digital domain ahead of the modulator, which removes the analogue attenuator from the path at the cost of trading bit depth for attenuation, so the profile noise floor rises as the control is turned down rather than staying fixed. The discrete class-A output contributes a low-order residue, but at -113 dB the ES9038PRO core still sets the floor.',
    provenanceNote:
      'The FPGA-based filter and volume architecture and the externally authored filter set come from Ferrum published material; the converter part is as described by Ferrum. Dynamic range, THD+N, output impedance and jitter are estimates rather than quoted specifications, so the entry is tagged ESTIMATED. The four filter options here are modelled stand-ins for the shipped set, matched by character rather than by coefficient.',
  }),

  makeDac({
    mfr: 'Grimm Audio',
    model: 'MU2',
    price: usd(28000, 'APPROXIMATE', 2023, 'Listed in euro by Grimm Audio; the USD figure here is a conversion of the roughly EUR 25,000 list price and is approximate.'),
    year: 2023,
    country: 'Netherlands',
    architecture: 'fpga-custom',
    core: 'Proprietary FPGA running a single long-window 4x DXD interpolation into the converter section',
    outputStage: 'op-amp',
    dynamicRangeDb: 122,
    thdPlusNDb: -110,
    crosstalkDb: -118,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 20,
    balanced: true,
    dsd: 'dsd128',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    filters: [
      {
        ...FILTERS.brickwall(4096),
        id: 'dxd-4x',
        label: 'Fixed 4x DXD long-window interpolation',
        cutoffRatio: 0.93,
        transitionRatio: 0.06,
        stopbandDb: -140,
      },
    ],
    internalOversampling: 4,
    jitterPsRms: 4,
    harmonicShape: HARMONIC_SHAPES.deltaSigma,
    nonlinear: { amount: 0.22, asymmetry: 0.06, headroomDb: 16 },
    confidence: 'ESTIMATED',
    accent: '#7fb069',
    tags: ['fpga', 'fixed-filter', 'clock-led', 'streamer-preamp'],
    notes:
      'There is no filter menu: one long-window interpolation to 4x DXD runs at every input rate, so pre- and post-ringing are fixed properties of the device instead of a user variable, and the reconstruction behaviour of a 44.1 kHz file and a 192 kHz file differ only in where the images land. The second modelled differentiator is the clock domain -- a 4 ps figure places clock sidebands well below the converter noise floor, so in this profile the audible character is carried by the analogue stage and the fixed filter, with jitter effectively removed as a variable.',
    provenanceNote:
      'The fixed 4x DXD upsampling architecture and the absence of a selectable filter bank come from Grimm Audio published descriptions. Grimm publishes very little quantitative data for this model, so dynamic range, THD+N, output impedance and the jitter figure are estimates; the converter part number is deliberately not named here because it could not be confirmed. Tagged ESTIMATED throughout.',
  }),

  makeDac({
    mfr: 'Antelope Audio',
    model: 'Zodiac Platinum DSD',
    price: usd(4500, 'APPROXIMATE', 2013, 'Original US retail was in the 4,000-5,000 USD region depending on power-supply bundling; the line is discontinued and the figure is approximate.'),
    year: 2013,
    country: 'Bulgaria',
    architecture: 'delta-sigma',
    core: 'Delta-sigma converter clocked by an oven-controlled oscillator with 64-bit direct-digital-synthesis clock generation',
    outputStage: 'op-amp',
    dynamicRangeDb: 120,
    thdPlusNDb: -108,
    crosstalkDb: -112,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 50,
    balanced: true,
    dsd: 'dsd128',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    filters: SIMPLE_FILTERS(),
    jitterPsRms: 2,
    noiseShaperOrder: 5,
    idleToneLevel: 0.08,
    harmonicShape: HARMONIC_SHAPES.deltaSigma,
    nonlinear: { amount: 0.28, asymmetry: 0.1, headroomDb: 15 },
    confidence: 'ESTIMATED',
    accent: '#c8b18a',
    tags: ['ocxo', 'clock-led', 'mastering-lineage', 'discontinued'],
    notes:
      'The engineering claim this profile exists to capture is the clock rather than the converter: an oven-stabilised oscillator driving a direct-digital-synthesis divider, giving a jitter figure roughly an order of magnitude below chip-clocked designs in this group. At 2 ps the clock sidebands sit below the converter noise floor, so the modelled result is not extra detail but the removal of a variable. Only two reconstruction filters are offered, both sharp, so unlike the Matrix and Questyle entries there is no slow-roll-off option to trade stopband rejection against ringing.',
    provenanceNote:
      'Acoustically Focused Clocking, the oven-controlled oscillator and 64-bit DDS clock generation come from Antelope Audio published material. The converter part is not named here because it could not be confirmed. Dynamic range is a deliberately conservative estimate -- Antelope has published a higher figure that could not be verified, and inflating it here would misrepresent the noise floor -- and THD+N, output impedance and the two-filter set are likewise estimates. Tagged ESTIMATED throughout.',
  }),
];
